/**
 * GrowthSignupLeadsTab — the Growth abandonment report (ADR-073 §2.6–§2.7, built by Amendment 5,
 * 5 October 2026). The recovery list for public sign-ups that have not paid: who started, where
 * they stopped, what they were about to buy, and when. A lead that paid is not here — payment
 * created the account and deleted the lead. Each lead expires 90 days after its last activity.
 *
 * Read-only. Errors go to the shared toast (CLAUDE.md — never inline, never raw).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { listAllSignupLeads, type LeadSegment, type SignupLeadsResult } from '../../lib/signup-leads-api';
import { useToast, toastFromError } from '../../components/ToastProvider';
import { ZoneCell, ZoneFanoutWarning } from '../../components/ZoneIndicators';
import { useZoneReadReport } from '../../components/ZoneContext';
import { currentTimeZone, formatInstant } from '../../lib/time-format';

const SEGMENT_LABEL: Record<LeadSegment, string> = {
  'never-started': 'Gave details, did not reach payment',
  'card-abandoned': 'Reached payment, left',
  'card-declined': 'Payment declined',
};
const TIER_LABEL: Record<string, string> = { T1: 'NexaGrowth', T2: 'NexaSuccess', T3: 'NexaContext' };
const TERM_LABEL: Record<string, string> = { monthly: 'monthly', quarterly: '3 months', annual: '12 months' };

const when = (iso: string) => (Number.isFinite(Date.parse(iso)) ? formatInstant(iso, currentTimeZone()).text : '—');

export function GrowthSignupLeadsTab() {
  const toast = useToast();
  const [result, setResult] = useState<SignupLeadsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [segment, setSegment] = useState<'all' | LeadSegment>('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setResult(await listAllSignupLeads());
    } catch (err) {
      toast.push({ variant: 'error', ...toastFromError(err) });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const rows = useMemo(
    () => (result?.leads ?? []).filter((l) => segment === 'all' || l.segment === segment),
    [result, segment],
  );
  const stats = result?.stats;
  // ADR-123 — this tab reads EVERY zone (leads live where their country derives), so it reports that
  // to the navigation bar's zone indicator itself, scoped, beside the page's home-only report.
  useZoneReadReport({ rows: loading ? null : rows, failedZones: result?.failedZones ?? [], scope: 'Sign-up leads' });

  return (
    <section className="growth-signup-leads" aria-label="Sign-up leads">
      <p className="growth-signup-leads-intro">
        Public sign-ups that have not paid. Nothing is created for a visitor until payment, so this is
        the only record of someone who stopped part-way — it is deleted the moment they pay, and expires
        90 days after their last activity.
      </p>

      {result && result.failedZones.length > 0 && (
        <ZoneFanoutWarning failedZones={result.failedZones} noun="sign-up leads" onRetry={() => void load()} />
      )}

      {stats && (
        <div className="growth-signup-leads-stats" role="group" aria-label="Sign-up lead counts">
          {([
            ['all', 'All', stats.total],
            ['never-started', 'Details only', stats.neverStarted],
            ['card-abandoned', 'Left at payment', stats.cardAbandoned],
            ['card-declined', 'Declined', stats.cardDeclined],
          ] as const).map(([key, label, n]) => (
            <button
              key={key}
              type="button"
              className={`growth-signup-leads-pill${segment === key ? ' active' : ''}`}
              aria-pressed={segment === key}
              onClick={() => setSegment(key)}
            >
              {label} <span className="growth-signup-leads-n">{n}</span>
            </button>
          ))}
          {stats.needsAttention > 0 && (
            <span className="growth-signup-leads-attention" role="status">
              {stats.needsAttention} paid but not provisioned — needs a person
            </span>
          )}
        </div>
      )}

      {loading && <p className="growth-signup-leads-empty" aria-live="polite">Loading sign-up leads…</p>}
      {!loading && rows.length === 0 && (
        <p className="growth-signup-leads-empty">No unpaid sign-ups{segment === 'all' ? '' : ' in this group'}.</p>
      )}
      {!loading && rows.length > 0 && (
        <table className="portal-table growth-signup-leads-table">
          <thead>
            <tr>
              <th scope="col">Last activity</th>
              <th scope="col">Zone</th>
              <th scope="col">Business</th>
              <th scope="col">Contact</th>
              <th scope="col">Where they stopped</th>
              <th scope="col">Plan</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((l) => (
              <tr key={`${l.zone}-${l.leadId}`} data-testid="signup-lead-row">
                <td>{when(l.lastActivityAt)}</td>
                <td><ZoneCell zone={l.zone} /> <span className="growth-signup-leads-country">{l.jurisdiction}</span></td>
                <td>
                  <div className="growth-signup-leads-biz">{l.businessName || '—'}</div>
                  <div className="growth-signup-leads-site">{l.website}</div>
                </td>
                <td>
                  <div>{l.ownerName || '—'}</div>
                  <div className="growth-signup-leads-site">{l.email}</div>
                </td>
                <td>
                  {l.failedAt ? 'Paid — provisioning failed (needs a person)' : SEGMENT_LABEL[l.segment] ?? l.segment}
                  {l.declineReason && <div className="growth-signup-leads-site">Reason: {l.declineReason}</div>}
                </td>
                <td>
                  {l.selectedTier
                    ? `${TIER_LABEL[l.selectedTier] ?? l.selectedTier}${l.commitment ? `, ${TERM_LABEL[l.commitment] ?? l.commitment}` : ''}${l.promoCode ? ` · ${l.promoCode}` : ''}`
                    : 'Not chosen'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
